import audienceApi from '@/api/audience'
import countingApi from '@/api/counting'
import Sentry from '@/3rd-parties/sentry'
import { CountingSchemes } from '@ahaslides-product/frontend-toolkit'
import { withReplayTicket } from '@/utils/replayTicket'

const PARTICIPANT_DOMAIN_NOT_ALLOWED = 'PARTICIPANT_DOMAIN_NOT_ALLOWED'
const PARTICIPANT_ACCESS_ALLOWED = { allowed: true, reason: null }

const participantAccessRefusal = (error) => (
  error && error.status === 403 && error.body && error.body.code === PARTICIPANT_DOMAIN_NOT_ALLOWED
    ? { allowed: false, reason: error.body.reason || null }
    : null
)

/**
 * Upserts the audience row for this join and resolves to the participant-access decision the API made on it:
 * { allowed: false, reason } when it refused the requester, else { allowed: true, reason: null } (also on any
 * other failure, so a missing or older API never blocks a join, unless requireSuccess asks it to throw).
 */
const createOrUpdateAudienceAction = async ({ rootGetters, commit, dispatch }, { requireSuccess = false, ...originalPayload }) => {
  const payload = withReplayTicket(originalPayload)
  try {
    let audience
    if (rootGetters['featureToggles/canUseCookieToLinkAudiences']) {
      try {
        audience = await audienceApi.createOrUpdateAudienceWithCookie(payload)
      } catch (error) {
        const refusal = participantAccessRefusal(error)
        if (refusal) return refusal
        Sentry.captureException(error, {
          extra: {
            message: 'createOrUpdateAudienceWithCookie failed',
            url: window.location.href,
          },
        })
        // In case the above createOrUpdateAudienceWithCookie doesn't work
        // (which has happened in elevatedminds' case because they don't allow forwarding POST requests),
        // fall back to the calling ahaslides' origin directly.
        // The API is an upsert so even if both API calls are effective nothing would happen
        // (but in theory the first API should have failed for us to get here).
        // In short, this is a defensive behavior aimed to remove any issues that may happen with white label forwarding
        // (the createOrUpdateAudienceWithCookie call needs same origin and therefore forwarding to our BE).
        audience = await audienceApi.createOrUpdateAudience(payload)
      }
    } else {
      audience = await audienceApi.createOrUpdateAudience(payload)
    }
    commit('setAudience', audience)
    dispatch('audienceCountJoinPresentationAction')
  } catch (error) {
    const refusal = participantAccessRefusal(error)
    if (refusal) return refusal
    Sentry.captureException(error)
    if (requireSuccess) throw error
  }
  return PARTICIPANT_ACCESS_ALLOWED
}
const setAudienceIdAction = ({ commit }, audienceId) => {
  commit('setAudienceId', audienceId)
}

const setQuestionerName = ({ commit }, name) => {
  commit('setQuestionerName', name)
}

const audienceJoinPresentationAction = async (_, payload) => {
  try {
    return await audienceApi.audienceJoinPresentation(payload)
  } catch (error) {
    // 0 = client network abort (mobile Safari / dropped connection); the status is
    // buried in extra.error, so beforeSend cannot filter it — drop it at the source.
    if (error?.status === 0) return null
    // Graceful fallback below (return null) — no corrective action taken here.
    Sentry.captureMessage(new Error('Audience Join Presentation Error'), {
      level: 'warning',
      extra: {
        error,
        payload,
      },
    })
  }
  return null
}

const audienceCountJoinPresentationAction = async ({ state, rootGetters }) => {
  if (!rootGetters['featureToggles/canUseEveryoneHasAnswered']) return
  try {
    const countingPayload = {
      counts: [
        CountingSchemes.everyoneAnsweredJoined({
          presentationId: state.presentation.id,
          presentationVersion: state.presentation.version,
          audienceId: state.audienceId,
        }),
      ],
    }
    await countingApi.sendCountingUnique(countingPayload)
  } catch (error) {
    // 0 = CORS error, or it hit browser cache — same guard as assignCookieAction
    if (error?.status === 0) return
    Sentry.captureMessage(new Error('Audience Join Counting Presentation Error'), {
      level: 'warning',
      extra: {
        error,
      },
    })
    // do nothing
  }
}

const audienceCountAnsweredSlideAction = async ({ state, getters }) => {
  if (!getters['featureToggles/canUseEveryoneHasAnswered']) return
  try {
    const countingPayload = {
      counts: [CountingSchemes.everyoneAnsweredSlide({
        presentationId: state.presentation.id,
        slideId: getters.activeActivity.id,
        slideVersion: getters.activeActivity.version,
        audienceId: state.audienceId,
      })],
    }
    countingApi.sendCountingUnique(countingPayload)
  } catch (error) {
    // Fire-and-forget analytics counting call — no corrective action taken here.
    Sentry.captureMessage(new Error('Audience Answered Counting Presentation Error'), {
      level: 'warning',
      extra: {
        error,
      },
    })
    // do nothing
  }
}

const checkParticipantByCookieAction = async (_, presentationId) => {
  try {
    return await audienceApi.checkParticipantByCookie({ presentationId })
  } catch (error) {
    // 0 = network cut-off / CORS / browser cache — same guard as
    // audienceCountJoinPresentationAction. The wrapper Error below hides the
    // real status from beforeSend, so it must be filtered here at the call site.
    if (error?.status === 0) return {}
    // Graceful fallback below (return {}) — no corrective action taken here.
    Sentry.captureMessage(new Error('checkParticipantByCookieAction Error'), {
      level: 'warning',
      extra: {
        error,
        presentationId,
      },
    })
  }
  return {}
}

export default {
  setAudienceIdAction,
  checkParticipantByCookieAction,
  setQuestionerName,
  createOrUpdateAudienceAction,
  audienceJoinPresentationAction,
  audienceCountJoinPresentationAction,
  audienceCountAnsweredSlideAction,
}
